import { Box, Button, Stack } from '@mui/material'
import { ColDef, ICellRendererParams, RowDragEvent } from 'ag-grid-community'
import {
  createLibraryTopic,
  deleteLibraryTopic,
  duplicateTopic,
  getLibraryTopics,
  updateLibraryTopic,
  updateTopicsOrder,
} from 'api/learnTopics'
import columnRenderers from 'components/column-renderers'
import ConfirmRemoveModal from 'components/modals/ConfirmRemoveModal'
import ModalWithTextAndIcon, {
  NameAndIconRequest,
} from 'components/modals/simple-modals/ModalWithTextAndIcon'
import NotificationSys from 'components/NotificationSystem'
import LoadingPlaceholder from 'components/placeholders/LoadingPlaceholder'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { useLearnLibraryCSVField } from 'features/LearnLibrary/hooks/useLearnLibraryCSVField'
import { useLearnLibraryMetadata } from 'features/LearnLibrary/hooks/useLearnLibraryMetadata'
import { Metadata } from 'features/Metadata'
import { createSummaryTagsColumn, useTagsFilterForDraggableTable } from 'features/Tags'
import { columnCreatedBy, columnUpdatedBy, getActionColumn, useDuplicateControl } from 'features/UI'
import { useChangeEntity } from 'hooks/useChangeEntity'
import { useExportArrayCSV } from 'hooks/useExportArrayCSV'
import { useRemoveModalControl } from 'hooks/useRemoveModalControl'
import moment from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { LearnTopic, Option, TableResponse, Tag } from 'types'
import { withIdParam } from 'utils'

export const LearnLibraryAdminPage = () => {
  const navigate = useNavigate()
  const [topics, setTopics] = useState<LearnTopic[]>([])

  const topicsQuery = useQuery(
    ['getLibraryTopics'],
    () => getLibraryTopics({ sort: { field: 'order', sort: 'asc' } }),
    {
      onSuccess: ({ data }: TableResponse<LearnTopic>) => {
        setTopics(data.rows)
      },
    },
  )

  const { DuplicateModal, setItem: setTopicToDuplicate } = useDuplicateControl<LearnTopic>({
    entityName: 'Topic',
    apiMethod: duplicateTopic,
    routeSuccess: routes.topicEdit,
  })

  const { idToRemove, openDeleteModal, closeDeleteModal, removeLoading, handleConfirmRemove } =
    useRemoveModalControl({
      deleteFunc: deleteLibraryTopic,
      successCallback: () => {
        setTopics((prev) => prev.filter((topic) => topic.id !== idToRemove))
      },
      warning: "Can't remove the learn library topic",
    })

  const onClickEdit = useCallback(
    (id: number) => {
      navigate(withIdParam(routes.topicEdit, id))
    },
    [navigate],
  )

  const handleDuplicateClick = useCallback(
    (id: number, value: ICellRendererParams<LearnTopic>) => {
      setTopicToDuplicate(value.data)
    },
    [setTopicToDuplicate],
  )

  const updateTopicMutation = useMutation(updateLibraryTopic)

  const handlePin = useCallback(
    async (id: number, props: ICellRendererParams<LearnTopic>) => {
      try {
        if (!props?.data) return
        const pinnedValue = !props.data.isPinned
        await updateTopicMutation.mutateAsync({
          ...props.data,
          isPinned: pinnedValue,
        })

        const message = pinnedValue ? 'Topic pinned successfully' : 'Topic unpinned successfully'
        NotificationSys.showSuccess(message)

        setTopics((prev) => {
          return prev.map((topic) => {
            if (topic.id === id) {
              return {
                ...topic,
                isPinned: pinnedValue,
              }
            }
            return topic
          })
        })
      } catch (e) {
        console.error(e)
      }
    },
    [updateTopicMutation],
  )

  const pinActionRender = useCallback((props: ICellRendererParams<LearnTopic>) => {
    const isPinned = props?.data?.isPinned
    return isPinned ? 'Unpin' : 'Pin'
  }, [])

  const actions = useMemo(() => {
    return [
      {
        name: 'Edit',
        onClick: onClickEdit,
      },
      { name: 'Delete', onClick: openDeleteModal },
      { name: 'Duplicate', onClick: handleDuplicateClick },
      { renderName: pinActionRender, onClick: handlePin },
    ]
  }, [handleDuplicateClick, onClickEdit, openDeleteModal, handlePin, pinActionRender])

  const filterTopicsByTags = useCallback(
    (tags: Option[]) => {
      return topics.filter((topic) => {
        return topic.articles.some((article) => {
          return article.tags.some((tag: Tag) => {
            return tags.some((selectedTag) => selectedTag.value === tag.id)
          })
        })
      })
    },
    [topics],
  )

  const { TagFilter, rowData, isDraggable } = useTagsFilterForDraggableTable<LearnTopic>(
    topics,
    'tags',
    filterTopicsByTags,
  )

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: 'Topic Name',
        rowDrag: isDraggable,
        cellRenderer: columnRenderers.createLink('name', routes.topicEdit),
        filter: 'agTextColumnFilter',
        colId: 'name',
        flex: 1,
        sortable: false,
      },
      { headerName: 'Number of Articles', field: 'numberOfArticles', sortable: false },
      createSummaryTagsColumn('articles'),
      {
        headerName: 'Pinned',
        cellRenderer: (props: ICellRendererParams<LearnTopic>) => {
          const isPinned = props?.data?.isPinned
          return isPinned ? 'Yes' : 'No'
        },
        minWidth: 160,
        colId: 'isPinned',
      },
      {
        headerName: 'Creation Date',
        cellRenderer: columnRenderers.createdAt,
        sortable: false,
      },
      columnCreatedBy(),
      {
        headerName: 'Last Edited',
        cellRenderer: columnRenderers.updatedAt,
        sortable: false,
      },
      columnUpdatedBy(),
      { ...getActionColumn(actions) },
    ]
  }, [actions, isDraggable])

  const [loading, setLoading] = useState(false)

  const [onSubmit, openAddModal, isOpenAddModal, closeAddModal] = useChangeEntity({
    setLoading,
    onSuccess: (response) => {
      navigate(withIdParam(routes.topicEdit, response.id))
    },
    requestFunc: createLibraryTopic,
    existedNameWarning:
      "Name must be unique. Can't create the learn library topic with existing name",
    warning: "Can't create the learn library topic",
  })

  const updateTopicsOrderMutation = useMutation(updateTopicsOrder)

  const handleRowDragEnd = useCallback(
    (e: RowDragEvent<LearnTopic>) => {
      const orderedIds = e.api
        .getRenderedNodes()
        .map((node) => (node.data ? node.data.id : null))
        .filter((id): id is number => id !== null)

      updateTopicsOrderMutation.mutate(
        { ids: orderedIds },
        {
          onSuccess: () => {
            const orderedItems = [...topics].sort(
              (a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id),
            )
            setTopics(orderedItems)
          },
          onError: () => {
            setTopics([...topics])
          },
        },
      )
    },
    [topics, updateTopicsOrderMutation],
  )

  const handleCreate = (values: NameAndIconRequest) => {
    onSubmit({ name: values.name, bannerMediaFileId: values.icon })
  }

  const metadata = useLearnLibraryMetadata(topicsQuery.data)

  const csvFields = useLearnLibraryCSVField()

  const { onExportCSV } = useExportArrayCSV(
    rowData,
    csvFields,
    `Learn Library Overview_${moment().format('YYYY-MM-DD')}.csv`,
  )

  return (
    <>
      <TableToolbar title="Learn Library Overview" exportCSVEnable onExportCSV={onExportCSV}>
        <Button variant="outlined" sx={{ minWidth: 120 }} onClick={openAddModal}>
          Add Learn Topic
        </Button>
      </TableToolbar>

      {topicsQuery.isLoading ? (
        <LoadingPlaceholder />
      ) : (
        <>
          <Metadata data={metadata} />

          <Stack flexDirection="row" mt={1} mb={2} className="gap-16">
            <Box minWidth="200px">{TagFilter}</Box>
          </Stack>

          <InfinityAgGrid
            columnDefs={columnDefs}
            rowDragManaged={isDraggable}
            animateRows
            rowDragEntireRow
            rowModelType="clientSide"
            rowData={rowData}
            onRowDragEnd={handleRowDragEnd}
            floatingFiltersHeight={0}
          />
        </>
      )}

      <ConfirmRemoveModal
        entityToRemove="Topic"
        loading={removeLoading}
        isOpen={idToRemove !== null}
        handleConfirm={handleConfirmRemove}
        handleClose={closeDeleteModal}
      />

      <ModalWithTextAndIcon
        title="Create Topic"
        label="Topic name"
        buttonText="Create"
        isOpen={isOpenAddModal}
        handleClose={closeAddModal}
        loading={loading}
        setLoading={setLoading}
        onSubmit={handleCreate}
      />

      {DuplicateModal}
    </>
  )
}
